/** @param {import('vue-i18n').Composer['t']} t */
export function lipSyncAlert(t, group, key) {
  return t(`playground.lipsyncAlerts.${group}.${key}`);
}

const NETWORK_ERROR_CODES = new Set([
  "ERR_NETWORK",
  "ENOTFOUND",
  "ECONNREFUSED",
  "ECONNRESET",
  "EAI_AGAIN",
  "ERR_INTERNET_DISCONNECTED",
  "ERR_CONNECTION_REFUSED",
  "ERR_CONNECTION_RESET",
  "ERR_NAME_NOT_RESOLVED",
]);

const NETWORK_HTTP_STATUSES = new Set([0, 502, 503, 504]);

const NETWORK_MESSAGE_RE =
  /network error|failed to fetch|fetch failed|network connection|net::|socket hang up|connection refused|connection reset|unable to connect|could not connect|dns|ssl|certificate|链接|连接失败|网络连接|网络错误|无法连接|接口.*(失败|错误|异常)|请求失败.*网络/i;

/**
 * API / transport layer connection failures (axios, gateway, backend copy).
 * @param {{ error?: { code?: string; message?: string; response?: { status?: number } }; res?: { message?: string; data?: { message?: string } }; message?: string }} ctx
 */
export function isLipSyncNetworkConnectionIssue(ctx = {}) {
  const { error, res } = ctx;
  const text = String(
    ctx.message ?? res?.data?.message ?? res?.message ?? error?.message ?? "",
  );
  const lower = text.toLowerCase();

  if (error) {
    if (error.code && NETWORK_ERROR_CODES.has(error.code)) return true;
    if (!error.response && error.request) return true;
    const status = error.response?.status;
    if (status !== undefined && NETWORK_HTTP_STATUSES.has(status)) return true;
  }

  if (NETWORK_MESSAGE_RE.test(text) || NETWORK_MESSAGE_RE.test(lower)) {
    return true;
  }

  return false;
}

/**
 * @param {{ error?: { code?: string; message?: string }; message?: string }} ctx
 */
export function isLipSyncRequestTimeoutIssue(ctx = {}) {
  const { error } = ctx;
  const text = String(ctx.message ?? error?.message ?? "");
  return error?.code === "ECONNABORTED" || /timeout|timed out|超时/i.test(text);
}

/**
 * Prefer i18n transport errors; otherwise API message or caller fallback.
 * @param {{ error?: unknown; res?: { message?: string; data?: { message?: string } }; fallback?: string }} ctx
 * @param {import('vue-i18n').Composer['t']} t
 */
export function resolveLipSyncNotifyError(ctx, t) {
  const { error, res, fallback = "" } = ctx;
  const apiMsg = String(res?.data?.message ?? res?.message ?? error?.message ?? "");

  if (isLipSyncNetworkConnectionIssue({ error, res, message: apiMsg })) {
    return lipSyncAlert(t, "errors", "networkError");
  }
  if (isLipSyncRequestTimeoutIssue({ error, message: apiMsg })) {
    return lipSyncAlert(t, "errors", "requestTimeout");
  }

  return apiMsg || fallback;
}

/**
 * @param {{ res?: { code?: number; message?: string; data?: { message?: string } }; error?: { code?: string; message?: string; response?: unknown; request?: unknown }; taskMessage?: string; isLoggedIn?: boolean; t: import('vue-i18n').Composer['t'] }} ctx
 */
export function resolveLipSyncGenerationError(ctx) {
  const { res, error, taskMessage, isLoggedIn, t } = ctx;
  const warn = (key) => lipSyncAlert(t, "warnings", key);
  const err = (key) => lipSyncAlert(t, "errors", key);
  const apiMsg = String(
    taskMessage || res?.data?.message || res?.message || error?.message || "",
  );
  const lower = apiMsg.toLowerCase();
  const code = res?.code;

  if (isLipSyncNetworkConnectionIssue({ error, res, message: apiMsg })) {
    return err("networkError");
  }
  if (isLipSyncRequestTimeoutIssue({ error, message: apiMsg })) {
    return err("requestTimeout");
  }

  if (code === 100101) {
    return "Please claim your daily free credits before generating.";
  }

  if (code === 100401) {
    return err("politicalSensitiveText");
  }
  if (code === 100402) {
    return err("politicalSensitiveImage");
  }

  if (code === 100100 || code === 100097) {
    return isLoggedIn
      ? warn("freeQuotaExhaustedLoggedIn")
      : warn("freeQuotaExhaustedAnonymous");
  }
  if (
    code === 100429 ||
    code === 429 ||
    /too many request|rate limit|too frequently|请求过于频繁|频繁/.test(lower)
  ) {
    return err("tooManyRequests");
  }

  if (/busy|繁忙|overload|服务繁忙|service is busy/.test(lower)) {
    return err("generationFailedBusy");
  }

  if (/upgrade.*pro|professional|专业版|unlimited generation/.test(lower)) {
    return warn("upgradeToProUnlimited");
  }

  if (code === 100013 || code === 100014) {
    return t("playground.lipsync.generator.toast.signInFirst");
  }

  return err("generationFailed");
}
